import React from 'react';
import PropTypes from 'prop-types';
import * as styles from './styles/styles.module.scss';
import GiftDisplayItem from '../GiftDisplayItem';
import GiftEditForm from '../GiftEditForm';

import { ServerCommunicationUtil } from '../../../utils/server_communication_util';

const STATUS_TO_WORD_HASH = {
  A: 'Active',
  E: 'Expired',
  R: 'Redeemed',
  U: 'Used'
};

class CouponManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      coupons: [],
      displayMessage: null,
      createNewCouponMode: false,
      isEditingGift: false
    };
  }

  handleOnInputChange = e => {
    e.preventDefault();
    this.setState({ couponText: e.currentTarget.value });
  };

  handleOnNewCodeInputChange = e => {
    e.preventDefault();
    this.setState({ newCouponText: e.currentTarget.value });
  };

  handleOnNewPriceChange = e => {
    e.preventDefault();
    this.setState({ newPrice: e.currentTarget.value });
  };

  handleOnNewPercentChange = e => {
    e.preventDefault();
    this.setState({ newPercentOff: e.currentTarget.value });
  };

  performCouponSearch = e => {
    this.getCoupons(this.state.couponText);
  };

  clearCouponSearch = e => {
    this.setState({ coupons: [], displayMessage: null, couponText: '' });
  };

  toggleCreateMode = e => {
    this.setState({ createNewCouponMode: !this.state.createNewCouponMode, coupons: [], displayMessage: null });
  };

  handleCouponSuccess = response => {
    if (response.length > 0 && response[0].planType) {
      this.setState({
        displayMessage: `Found ${response.length} gift subscription${response.length > 1 ? 's' : ''}`,
        coupons: response
      });
    } else {
      this.setState({
        displayMessage: `Found ${response.length} coupon${response.length > 1 ? 's' : ''}`,
        coupons: response
      });
    }
  };

  handleNewCouponCreateSuccess = response => {
    this.setState({
      coupons: [response[0]],
      displayMessage: 'Coupon created',
      createNewCouponMode: false,
      newPercentOff: null,
      newCouponText: null,
      newPrice: null
    });
  };

  handleCouponUpdateSuccess = response => {
    // replace coupon in copy of state array
    const mappedCoupons = this.state.coupons.map((coup, idx) => {
      if (coup.id === response[0].id) {
        return response[0];
      }
      return coup;
    });
    this.setState({ coupons: mappedCoupons, displayMessage: 'Updated coupon' });
  };

  handleGiftUpdateSuccess = response => {
    this.setState({ coupons: response, isEditingGift: false, displayMessage: 'Updated gift subscription' });
  };

  handleCouponFailure = err => {
    if (err && err.status == 404) {
      this.setState({ displayMessage: 'No coupons found' });
    } else {
      console.error("err", err);
      alert(`An error occurred: ${err.statusText}`);
    }
  };

  getCoupons(couponCode) {
    ServerCommunicationUtil.getApiEndpoint(
      '/api/alltrails/admin/coupons',
      { couponCode },
      this.handleCouponSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  updateCoupon(couponId, params) {
    ServerCommunicationUtil.putApiEndpoint(
      `/api/alltrails/admin/coupons/${couponId}`,
      params,
      this.handleCouponUpdateSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  updateGift(giftId, params) {
    ServerCommunicationUtil.putApiEndpoint(
      `/api/alltrails/gift/${giftId}`,
      params,
      this.handleGiftUpdateSuccess,
      () => alert('An error occurred'),
      () => {}
    );
  }

  saveNewCouponClick = e => {
    e.preventDefault();
    if (this.state.newCouponText && this.state.newPrice && this.state.newPercentOff) {
      this.saveNewCoupon({
        couponCode: this.state.newCouponText,
        price: this.state.newPrice,
        percentOff: this.state.newPercentOff
      });
    } else {
      alert('Missing information to create a new coupon.');
    }
  };

  saveNewCoupon(params) {
    ServerCommunicationUtil.postApiEndpoint(
      `/api/alltrails/admin/coupons`,
      params,
      this.handleNewCouponCreateSuccess,
      this.handleCouponFailure,
      () => {}
    );
  }

  expireCoupon = couponId => {
    this.updateCoupon(couponId, { status: 'E' });
  };

  deactivateCoupon = couponId => {
    this.updateCoupon(couponId, { status: 'R' });
  };

  renderItemDisplay(item) {
    if (item.planType) {
      return this.renderGiftDiv(item);
    }
    return this.renderCouponDiv(item);
  }

  handleGiftResendSuccess = response => {
    const gift = [...this.state.coupons][0];
    gift.resent = true;
    this.setState({ coupons: [gift] });
  };

  handleResendGiftClick = giftId => {
    ServerCommunicationUtil.postApiEndpoint(
      `/api/alltrails/gift/${giftId}/resend`,
      {},
      this.handleGiftResendSuccess,
      () => alert('An error occurred'),
      () => {}
    );
  };

  handleDeactivateGiftClick = giftId => {
    ServerCommunicationUtil.deleteApiEndpoint(
      `/api/alltrails/gift/${giftId}`,
      {},
      this.clearCouponSearch,
      () => alert('An error occurred'),
      () => {}
    );
  };

  handleSaveGiftClick = newGift => {
    const updateParams = (({ senderEmail, senderName, recipientName, recipientEmail, deliveryDate, message }) => ({
      senderEmail,
      senderName,
      recipientName,
      recipientEmail,
      deliveryDate,
      message
    }))(newGift);
    this.updateGift(newGift.id, updateParams);
  };

  handleEditGiftClick = e => {
    this.setState({ isEditingGift: !this.state.isEditingGift });
  };

  renderGiftDiv(gift) {
    if (this.state.isEditingGift) {
      return <GiftEditForm gift={gift} handleEditGiftClick={this.handleEditGiftClick} handleSaveGiftClick={this.handleSaveGiftClick} />;
    }
    return (
      <GiftDisplayItem
        gift={gift}
        handleEditGiftClick={this.handleEditGiftClick}
        handleResendGiftClick={this.handleResendGiftClick}
        handleDeactivateGiftClick={this.handleDeactivateGiftClick}
      />
    );
  }

  renderCouponDiv(coupon) {
    let expireBtn = null;
    if (coupon.metadata.status === 'A') {
      // Subscription coupons can be used by many users, so we want them to be "expired"
      if (coupon.type === 'SubscriptionCoupon') {
        expireBtn = (
          <button className={styles.expireBtn} onClick={() => this.expireCoupon(coupon.id)}>
            Expire
          </button>
        );
        // gift coupons may be bought in error and refunded, in which case we want to set them to be "redeemed"
      } else if (coupon.type === 'GiftCoupon') {
        expireBtn = (
          <button className={styles.expireBtn} onClick={() => this.deactivateCoupon(coupon.id)}>
            Deactivate
          </button>
        );
      }
    }
    return (
      <div className={styles.couponCard}>
        <h4>{`Code: ${coupon.code}`}</h4>
        <h4>{`Type: ${coupon.type}`}</h4>
        <h4>{`Price: $${coupon.discountedPricePerYear}`}</h4>
        <h4>{`Percent off: ${coupon.discountPercentage}%`}</h4>
        <h4>{`Status: ${STATUS_TO_WORD_HASH[coupon.metadata.status]}`}</h4>
        {expireBtn}
      </div>
    );
  }

  renderCouponsContainer() {
    if (!this.createNewCouponMode && this.state.coupons.length > 0) {
      const couponElements = this.state.coupons.map(coup => this.renderItemDisplay(coup));
      return <div className={styles.couponBoxContainer}>{couponElements}</div>;
    }
    return null;
  }

  renderNewCouponForm() {
    if (this.state.createNewCouponMode) {
      return (
        <div>
          <button className={styles.searchButtons} onClick={this.toggleCreateMode}>Cancel</button>
          <button className={styles.searchButtons} onClick={this.saveNewCouponClick}>Save</button>
          <div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewCodeInputChange}
                value={this.state.newCouponText}
                className={styles.searchBox}
                placeholder="Enter new coupon code"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewPriceChange}
                value={this.state.newPrice}
                className={styles.searchBox}
                placeholder="Enter purchase price"
              />
            </div>
            <div className={styles.inputEditContainer}>
              <input
                onChange={this.handleOnNewPercentChange}
                value={this.state.newPercentOff}
                className={styles.searchBox}
                placeholder="Enter percent discount"
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  renderButtonSearchBar() {
    if (!this.state.createNewCouponMode) {
      return (
        <div>
          <input onChange={this.handleOnInputChange} value={this.state.couponText} className={styles.searchBox} placeholder="Enter coupon code" />
          <button className={styles.searchButtons} onClick={this.performCouponSearch}>
            Find coupons
          </button>
          <button className={styles.searchButtons} onClick={this.clearCouponSearch}>
            Clear search
          </button>
          <button className={styles.searchButtons} onClick={this.toggleCreateMode}>
            Create coupon
          </button>
          <span className={styles.msgDisplay}>{this.state.displayMessage}</span>
        </div>
      );
    }
    return null;
  }

  renderTitleDiv() {
    let title = null;
    if (this.state.createNewCouponMode) {
      title = 'Creating a coupon';
    } else {
      title = 'Searching for a coupon';
    }
    return <h2>{title}</h2>;
  }

  render() {
    return (
      <div className={styles.container}>
        {this.renderTitleDiv()}
        {this.renderButtonSearchBar()}
        {this.renderCouponsContainer()}
        {this.renderNewCouponForm()}
      </div>
    );
  }
}

export default CouponManager;
